const APPLE_PLATFORM = /^(macOS|iOS|Mac|iPhone|iPad|iPod)/i;

type ShortcutParts = {
  key: string;
  modifier?: boolean;
  shift?: boolean;
};

class BrowserHelpers {
  static isSafari(): boolean {
    return (
      typeof navigator !== "undefined" &&
      navigator.userAgent.includes("AppleWebKit") &&
      !navigator.userAgent.includes("Chrome")
    );
  }

  /**
   * Navigates the current tab to `url`. A thin wrapper around `window.location.assign`
   * so call sites can be unit tested without touching jsdom's non-configurable
   * `window.location`.
   */
  static navigateTo(url: string): void {
    window.location.assign(url);
  }

  static isApplePlatform(): boolean {
    if (typeof navigator === "undefined") return false;

    // `userAgentData.platform` is the supported replacement for the deprecated
    // `navigator.platform`. Chromium-only so far, hence the fallbacks.
    const reported =
      (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
      navigator.platform;

    if (reported) return APPLE_PLATFORM.test(reported);

    // Last resort. Deliberately does not match jsdom's `(darwin)` user agent:
    // tests default to the Ctrl platform and opt into Apple explicitly.
    return /\b(Macintosh|Mac OS X|iPhone|iPad|iPod)\b/.test(navigator.userAgent);
  }

  /** The ProseMirror key prefix for this platform's modifier. */
  static primaryModifierKey(): "Meta" | "Ctrl" {
    return BrowserHelpers.isApplePlatform() ? "Meta" : "Ctrl";
  }

  /** `⇧⌘Z` on a Mac, `Ctrl+Shift+Z` elsewhere. */
  static formatShortcut({ key, modifier = true, shift = false }: ShortcutParts): string {
    if (BrowserHelpers.isApplePlatform()) {
      return `${shift ? "⇧" : ""}${modifier ? "⌘" : ""}${key}`;
    }
    return [modifier ? "Ctrl" : null, shift ? "Shift" : null, key].filter(Boolean).join("+");
  }
}

export default BrowserHelpers;
